import React, { useState, useCallback } from 'react';
import { Helmet } from 'react-helmet';

import { PAGE_CONTENT_CLASS } from '../../components/layout/PageContent';
import PageHeader from '../../components/PageHeader';

import LicenseCard from './components/LicenceCard';
import licenses from '../../data/loadLicenses';
import PurchaseLicenseModal from './components/PurchaseModal';
import licenseService from '../../services/licenseService';

/** Deterministic display order: Individual Essencial → Profissional → Elite, then Empresarial */
const LICENSE_DISPLAY_ORDER = [
  'individual_essencial',
  'individual_profissional',
  'individual_elite',
  'empresarial_essencial',
  'empresarial_profissional',
  'empresarial_elite'
];

const sortLicensesByDisplayOrder = (list) => {
  const orderMap = Object.fromEntries(
    LICENSE_DISPLAY_ORDER.map((type, index) => [type, index])
  );
  return [...list].sort((a, b) => {
    const ia = orderMap[a.license_type] ?? LICENSE_DISPLAY_ORDER.length;
    const ib = orderMap[b.license_type] ?? LICENSE_DISPLAY_ORDER.length;
    return ia - ib;
  });
};

/** License types that get the "Melhor escolha" highlight (one per row) */
const BEST_CHOICE_TYPES = new Set(['individual_profissional', 'empresarial_profissional']);

const LicenseSelectionScreen = () => {
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [purchaseMode, setPurchaseMode] = useState('one_time');
  const [changePlan, setChangePlan] = useState(false);

  const sortedLicenses = sortLicensesByDisplayOrder(licenses);

  const handlePurchase = useCallback((license) => {
    setPurchaseMode('one_time');
    setChangePlan(false);
    setSelectedLicense(license);
  }, []);

  const handleSubscribe = useCallback(async (license) => {
    setPurchaseMode('subscription');
    setSelectedLicense(license);

    const response = await licenseService.getMySubscription();
    const active = response?.subscription;
    const hasDifferentActivePlan =
      active &&
      !active.cancel_at_period_end &&
      active.license_type !== license.license_type &&
      (active.status === 'authorized' || active.status === 'active');

    setChangePlan(Boolean(hasDifferentActivePlan));
  }, []);

  const handleClose = useCallback(() => {
    setSelectedLicense(null);
  }, []);

  return (
    <>
      <Helmet>
        <title>Licenças Individuais e Empresariais</title>
        <meta
          name="description"
          content="Escolha a licença ideal para geração e assinatura de documentos"
        />
      </Helmet>

      <div className={PAGE_CONTENT_CLASS}>
        <PageHeader
          title="Licenças Individuais e Empresariais"
          subtitle="Escolha o plano ideal para gerar e assinar documentos com agilidade"
          icon="Award"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {sortedLicenses.map((license) => (
            <LicenseCard
              key={license.license_code}
              license={license}
              isRecommended={BEST_CHOICE_TYPES.has(license.license_type)}
              onPurchase={handlePurchase}
              onSubscribe={handleSubscribe}
            />
          ))}
        </div>
      </div>

      {selectedLicense && (
        <PurchaseLicenseModal
          license={selectedLicense}
          purchaseMode={purchaseMode}
          changePlan={changePlan}
          onClose={handleClose}
          onConfirm={() => {
            console.log('Proceed to payment:', selectedLicense);
          }}
        />
      )}
    </>
  );
};

export default LicenseSelectionScreen;
